import React, { useEffect, useMemo, useState } from "react";
import { DeviceKeys, Devices } from "@app/shared/src/api/api.gen";
import { UserError } from "@app/shared/src/api/api_client";
import {
    CollisionSensitivity,
    MountedOnType,
    MountedOnTypeDisplayNames,
    getCollisionSensitivityDisplayName,
} from "@app/shared/src/models";
import {
    Btn,
    Combo,
    Modal,
    Note,
    OptionButton,
    Slider,
    Spinner,
    Tag,
    TextInput,
    useToast,
} from "@app/shared/src/components_v2";
import { MAX_SIDE_PASS_LANES, MIN_SIDE_PASS_LANES } from "@app/shared/src/helpers/sidePassGeometry";
import {
    AddDeviceDraft,
    FEET_TO_METRES,
    STEP_TITLES,
    WIZARD_STEPS,
    WizardStep,
    draftToPlan,
    initialDraft,
    stepBlocker,
} from "../../helpers/addDeviceDraft";
import { provisionDevice } from "../../helpers/provisionDevice";
import { useFleet } from "../providers/FleetProvider";
import { useOrg } from "../providers/OrgProvider";
import { useFleetDefaultsRecord } from "../../hooks/useFleetDefaultsRecord";
import { DeviceLabel } from "./DeviceLabel";
import { Toggle } from "./Toggle";

/**
 * Adding a truck, end to end, without leaving this console.
 *
 * This used to be a single key field that checked the key and then sent the director to the main
 * dashboard's ten-step wizard. That hand-off was the honest thing to do while adoption lived
 * elsewhere, but it also meant the one workflow a fleet does most often was the one workflow this
 * console could not finish — and the dashboard asks for map placement and zone drawing that a TMA,
 * which is a truck rather than a site, has no use for.
 *
 * So the questions here are the ones an attenuator actually needs: whose group, what the truck is
 * called, how the unit is mounted, the footprint it defends, and which alerts to arm. Everything
 * else the dashboard asks is either fixed for a TMA or already stated once in the fleet defaults,
 * which is where every value on these screens starts.
 *
 * Nothing is written until Review. Each step is a plain edit of a local draft, so backing up and
 * changing an answer costs nothing and leaves no half-built truck behind — see provisionDevice for
 * why the commit itself cannot offer the same guarantee once it starts.
 */

const feetLabel = (v: string) => `${v || "0"} ft`;

/**
 * A wizard question: label and hint on top, the control underneath, full width.
 *
 * Deliberately NOT FRow. That row puts the control beside the title, which works on a settings page
 * with the width to share but not in a 560px modal — the title column was left ~150px next to a
 * 240px-minimum slider, wrapping every hint into a squashed sliver. FRow also carries px-5 to align
 * inside a Card, which inside the modal's own px-6 only doubled the padding. Stacking gives the
 * text the modal's whole width and every control one aligned left edge.
 *
 * `action` sits on the label line's right — the natural place for a row's on/off toggle, with the
 * control it enables appearing underneath.
 */
const WRow: React.FC<{
    title: React.ReactNode;
    sub?: React.ReactNode;
    action?: React.ReactNode;
    children?: React.ReactNode;
}> = ({ title, sub, action, children }) => (
    <div className="flex flex-col gap-2">
        <div className="flex items-center gap-4">
            <span className="min-w-0 flex-1">
                <span className="block text-[13.5px] font-medium">{title}</span>
                {sub && <span className="mt-0.5 block text-[12px] leading-snug text-[var(--muted)]">{sub}</span>}
            </span>
            {action}
        </div>
        {children}
    </div>
);

/** A review line: label left, chosen value right. No card padding — the modal supplies its own. */
const ReviewRow: React.FC<{ title: React.ReactNode; sub?: React.ReactNode; children?: React.ReactNode }> = ({
    title,
    sub,
    children,
}) => (
    <div className="flex items-center gap-4 border-t border-[var(--line)] py-2.5 first:border-t-0">
        <span className="min-w-0 flex-1">
            <span className="block text-[13.5px] font-medium">{title}</span>
            {sub && <span className="mt-0.5 block text-[12px] leading-snug text-[var(--muted)]">{sub}</span>}
        </span>
        {children}
    </div>
);

export const AddDeviceWizard: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
    const toast = useToast();
    const { org } = useOrg();
    const { groups, reload } = useFleet();
    const { defaults, isLoading: defaultsLoading } = useFleetDefaultsRecord();

    const [step, setStep] = useState<WizardStep>("key");
    const [draft, setDraft] = useState<AddDeviceDraft>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string>();
    const [progress, setProgress] = useState<string>();
    /** Pieces the commit could not finish. Held on screen rather than toasted away. */
    const [incomplete, setIncomplete] = useState<string[]>();

    // Seeded once the fleet record arrives; until then the wizard has no defaults to start from and
    // showing blank fields would invite a director to type values they have already stated.
    useEffect(() => {
        if (open && defaults && !draft) setDraft(initialDraft(defaults));
    }, [open, defaults, draft]);

    const close = () => {
        if (busy) return;
        setStep("key");
        setDraft(undefined);
        setError(undefined);
        setProgress(undefined);
        setIncomplete(undefined);
        onClose();
    };

    const set = <K extends keyof AddDeviceDraft>(key: K, value: AddDeviceDraft[K]) =>
        setDraft((d) => (d ? { ...d, [key]: value } : d));

    const index = WIZARD_STEPS.indexOf(step);
    const blocker = draft ? stepBlocker(step, draft) : "Loading your fleet defaults…";

    const back = () => {
        setError(undefined);
        if (index > 0) setStep(WIZARD_STEPS[index - 1]);
    };

    const next = () => {
        setError(undefined);
        if (index < WIZARD_STEPS.length - 1) setStep(WIZARD_STEPS[index + 1]);
    };

    /** Check the key against the server. Its device id is what the commit later attaches. */
    const checkKey = async () => {
        if (!draft || busy) return;
        const key = draft.deviceKey.trim();
        if (!key) return;

        setBusy(true);
        setError(undefined);
        try {
            // Unassociated first: a hit here is the happy path and gives us the id straight away.
            let lookupFailure: unknown;
            const free = await Devices.getUnassociatedDevice(key, org?.id).catch((err) => {
                lookupFailure = err;
                return undefined;
            });
            if (free?.id) {
                setDraft((d) => (d ? { ...d, verifiedDeviceId: free.id } : d));
                return;
            }

            // "Already on another truck" and "not a key we know" need different things from the
            // director. The search is super-admin only, so a 409 is the fallback that still names
            // where the device went — the org rather than the truck, hence second to a search hit.
            const found = await DeviceKeys.search(key).catch(() => undefined);
            const conflict = lookupFailure instanceof UserError && lookupFailure.status === 409 ? lookupFailure : undefined;
            if (!found?.allocatedKeys?.length && conflict) {
                setError(conflict.message);
            } else if (!found) {
                setError("That key is not available to add. It may already be on another truck, or it may be mistyped — keys are 7 letters and numbers, like ABC1234.");
            } else if (found.allocatedKeys?.length) {
                const holder = found.allocatedKeys[0].name;
                setError(
                    holder
                        ? `That key belongs to ${holder}. Support can move it if it should be yours.`
                        : "That key is already on another truck. Support can move it if it should be yours.",
                );
            } else {
                setError("No device matches that key. Check the label on the underside of the unit, next to the Ethernet jack — keys are 7 letters and numbers, like ABC1234.");
            }
        } catch (err) {
            setError(err instanceof Error ? err.message : "Could not check that key");
        } finally {
            setBusy(false);
        }
    };

    const commit = async () => {
        if (!draft || !defaults || !org?.id || busy) return;
        const plan = draftToPlan(draft, defaults);
        if (!plan) {
            setError("Something above is still incomplete. Step back and check each page.");
            return;
        }

        setBusy(true);
        setError(undefined);
        try {
            const result = await provisionDevice(plan, org.id, defaults, setProgress);
            await reload();

            if (result.warnings.length) {
                setIncomplete(result.warnings);
                return;
            }

            toast(`${plan.truckName} is set up and ready for a cab card`);
            close();
        } catch (err) {
            setError(err instanceof Error ? err.message : "The truck could not be added");
        } finally {
            setBusy(false);
            setProgress(undefined);
        }
    };

    const groupOptions = useMemo(
        () => groups.map((g) => ({ value: g.id!, label: g.name ?? "Unnamed group", sub: `${g.facilityCount} location(s)` })),
        [groups],
    );

    const isLast = step === "review";
    const { title, subtitle } = incomplete
        ? { title: "Added, but not finished", subtitle: "The truck exists — these parts still need doing" }
        : STEP_TITLES[step];

    return (
        <Modal
            open={open}
            onClose={close}
            title={title}
            subtitle={subtitle}
            footer={
                incomplete ? (
                    <Btn onClick={close}>Done</Btn>
                ) : (
                <>
                    <Btn tone="quiet" onClick={index === 0 ? close : back} disabled={busy}>
                        {index === 0 ? "Cancel" : "Back"}
                    </Btn>
                    {step === "key" && !draft?.verifiedDeviceId ? (
                        <Btn onClick={checkKey} disabled={busy || !draft?.deviceKey.trim()}>
                            {busy ? "Checking…" : "Check key"}
                        </Btn>
                    ) : (
                        <Btn onClick={isLast ? commit : next} disabled={busy || Boolean(blocker)}>
                            {busy ? progress ?? "Working…" : isLast ? "Add device" : "Next"}
                        </Btn>
                    )}
                </>
                )
            }
        >
            {incomplete ? (
                <div className="flex flex-col gap-3">
                    {incomplete.map((warning) => (
                        <Note key={warning} tone="warn">
                            {warning}
                        </Note>
                    ))}
                </div>
            ) : (
            <>
            <StepDots current={index} />

            {defaultsLoading || !draft ? (
                <div className="flex justify-center py-8">
                    <Spinner />
                </div>
            ) : (
                <>
                    {step === "key" && (
                        <>
                            <label className="text-[12px] text-[var(--muted)]">Device key</label>
                            <TextInput
                                value={draft.deviceKey}
                                onChange={(e) => setDraft({ ...draft, deviceKey: e.target.value, verifiedDeviceId: undefined })}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter") checkKey();
                                }}
                                placeholder="ABC1234"
                                autoFocus
                            />
                            {draft.verifiedDeviceId && (
                                <div className="flex items-center gap-2">
                                    <Tag tone="safe">Key found</Tag>
                                    <span className="text-[12.5px] text-[var(--muted)]">
                                        Unclaimed and ready to set up
                                    </span>
                                </div>
                            )}
                            <div className="flex flex-col items-center gap-2 pt-1">
                                <DeviceLabel />
                                <p className="text-center text-[12px] leading-relaxed text-[var(--muted)]">
                                    The label is on the underside of the unit, next to the Ethernet jack. The key is the
                                    large 7-character code under the Omnisight mark — not the HVIN, FCC ID or IC ID.
                                </p>
                            </div>
                        </>
                    )}

                    {step === "group" && (
                        <>
                            {groupOptions.length > 0 && (
                                <WRow title="Existing group" sub="Search by name">
                                    <Combo
                                        options={groupOptions}
                                        value={draft.group?.kind === "existing" ? draft.group.id : ""}
                                        onChange={(id) =>
                                            set("group", {
                                                kind: "existing",
                                                id,
                                                name: groupOptions.find((g) => g.value === id)?.label ?? "",
                                            })
                                        }
                                        ariaLabel="Facility group"
                                        placeholder="Search groups…"
                                    />
                                </WRow>
                            )}

                            <WRow title="Or add a new group" sub="A yard, a region, a contract — whatever you divide the fleet by">
                                <TextInput
                                    value={draft.group?.kind === "new" ? draft.group.name : ""}
                                    onChange={(e) => set("group", { kind: "new", name: e.target.value })}
                                    placeholder="North yard"
                                    aria-label="New group name"
                                />
                            </WRow>

                            {groupOptions.length === 0 && (
                                <Note>This is your first group, so there is nothing to pick from yet — name one above.</Note>
                            )}
                        </>
                    )}

                    {step === "identity" && (
                        <>
                            <label className="text-[12px] text-[var(--muted)]">Truck number or equipment name</label>
                            <TextInput
                                value={draft.truckName}
                                onChange={(e) => set("truckName", e.target.value)}
                                placeholder="Truck 412"
                                autoFocus
                            />
                            <Note>
                                This names both the unit and its location. Drivers see it on the cab card, so use whatever the
                                crew calls this truck rather than a serial number.
                            </Note>
                        </>
                    )}

                    {step === "mounting" && (
                        <>
                            <WRow title="Mounting height" sub="Ground to the unit">
                                <span className="flex items-center gap-2">
                                    <TextInput
                                        value={draft.heightFeet}
                                        onChange={(e) => set("heightFeet", e.target.value)}
                                        inputMode="decimal"
                                        className="w-[92px] text-right"
                                        aria-label="Mounting height in feet"
                                    />
                                    <span className="text-[13px] text-[var(--muted)]">ft</span>
                                </span>
                            </WRow>

                            <WRow title="Offset from centre" sub="Negative is left of centre, 0 is centred">
                                <span className="flex items-center gap-2">
                                    <TextInput
                                        value={draft.offsetFeet}
                                        onChange={(e) => set("offsetFeet", e.target.value)}
                                        inputMode="decimal"
                                        className="w-[92px] text-right"
                                        aria-label="Offset from centre in feet"
                                    />
                                    <span className="text-[13px] text-[var(--muted)]">ft</span>
                                </span>
                            </WRow>

                            <WRow title="Mounted on">
                                <div className="flex flex-wrap gap-2">
                                    {MOUNTED_ON_CHOICES.map((value) => (
                                        <OptionButton
                                            key={value}
                                            selected={draft.mountedOn === value}
                                            onClick={() => set("mountedOn", value)}
                                        >
                                            {MountedOnTypeDisplayNames[value]}
                                        </OptionButton>
                                    ))}
                                </div>
                            </WRow>
                        </>
                    )}

                    {step === "area" && (
                        <>
                            <WRow title="Width" sub="Across the back of the equipment">
                                <span className="flex items-center gap-2">
                                    <TextInput
                                        value={draft.widthFeet}
                                        onChange={(e) => set("widthFeet", e.target.value)}
                                        inputMode="decimal"
                                        className="w-[92px] text-right"
                                        aria-label="Protected width in feet"
                                    />
                                    <span className="text-[13px] text-[var(--muted)]">ft</span>
                                </span>
                            </WRow>

                            <WRow title="Length" sub="How far back the protected area reaches">
                                <span className="flex items-center gap-2">
                                    <TextInput
                                        value={draft.lengthFeet}
                                        onChange={(e) => set("lengthFeet", e.target.value)}
                                        inputMode="decimal"
                                        className="w-[92px] text-right"
                                        aria-label="Protected length in feet"
                                    />
                                    <span className="text-[13px] text-[var(--muted)]">ft</span>
                                </span>
                            </WRow>

                            <Note>
                                Started from your fleet default of {feetLabel(String((defaults!.tmaWidthMetres / FEET_TO_METRES).toFixed(1)))} by{" "}
                                {feetLabel(String((defaults!.tmaLengthMetres / FEET_TO_METRES).toFixed(1)))}. Adjust it if this
                                truck is built differently — it is the area the impending-collision alert defends.
                            </Note>
                        </>
                    )}

                    {step === "alerts" && (
                        <>
                            <div className="text-[12.5px] font-semibold">Impending collision</div>
                            <Note>Always on. It is the reason the unit is on the truck, and it cannot be switched off.</Note>

                            <WRow title="Minimum approach speed" sub="Slower approaches never alert">
                                <MphInput
                                    value={draft.approachMph}
                                    onChange={(v) => set("approachMph", v)}
                                    label="Minimum approach speed"
                                />
                            </WRow>

                            <WRow title="Driver reaction time" sub="Lead time added ahead of impact">
                                <span className="flex items-center gap-2">
                                    <TextInput
                                        value={draft.reactionSeconds}
                                        onChange={(e) => set("reactionSeconds", e.target.value)}
                                        inputMode="decimal"
                                        className="w-[92px] text-right"
                                        aria-label="Driver reaction time in seconds"
                                    />
                                    <span className="text-[13px] text-[var(--muted)]">s</span>
                                </span>
                            </WRow>

                            <WRow title="Sensitivity" sub="How much confirmation before firing">
                                <Slider
                                    options={SENSITIVITY_STOPS}
                                    value={String(draft.sensitivity)}
                                    onChange={(v) => set("sensitivity", Number(v) as CollisionSensitivity)}
                                    lowLabel="Fewer false alarms"
                                    highLabel="Earlier warning"
                                />
                            </WRow>

                            <WRow
                                title="Speeding"
                                sub={draft.speedingEnabled ? "Vehicle passes over the threshold" : "Off"}
                                action={
                                    <Toggle
                                        on={draft.speedingEnabled}
                                        onChange={(v) => set("speedingEnabled", v)}
                                        label="Speeding alert"
                                    />
                                }
                            >
                                {draft.speedingEnabled && (
                                    <MphInput
                                        value={draft.speedingMph}
                                        onChange={(v) => set("speedingMph", v)}
                                        label="Speeding threshold"
                                    />
                                )}
                            </WRow>

                            <WRow
                                title="Left pass"
                                sub={draft.leftPassEnabled ? `${draft.leftPassLanes} lane(s) out` : "Off"}
                                action={
                                    <Toggle
                                        on={draft.leftPassEnabled}
                                        onChange={(v) => set("leftPassEnabled", v)}
                                        label="Left pass alert"
                                    />
                                }
                            >
                                {draft.leftPassEnabled && (
                                    <LaneStepper value={draft.leftPassLanes} onChange={(v) => set("leftPassLanes", v)} side="Left" />
                                )}
                            </WRow>

                            <WRow
                                title="Right pass"
                                sub={draft.rightPassEnabled ? `${draft.rightPassLanes} lane(s) out` : "Off"}
                                action={
                                    <Toggle
                                        on={draft.rightPassEnabled}
                                        onChange={(v) => set("rightPassEnabled", v)}
                                        label="Right pass alert"
                                    />
                                }
                            >
                                {draft.rightPassEnabled && (
                                    <LaneStepper value={draft.rightPassLanes} onChange={(v) => set("rightPassLanes", v)} side="Right" />
                                )}
                            </WRow>

                            {(draft.leftPassEnabled || draft.rightPassEnabled) && (
                                <WRow title="Side-pass minimum speed" sub="Shared by both sides">
                                    <MphInput
                                        value={draft.sidePassMph}
                                        onChange={(v) => set("sidePassMph", v)}
                                        label="Side pass minimum speed"
                                    />
                                </WRow>
                            )}
                        </>
                    )}

                    {step === "review" && (
                        <>
                            <ReviewRow title="Device key">
                                <span className="font-mono text-[13px]">{draft.deviceKey.trim()}</span>
                            </ReviewRow>
                            <ReviewRow title="Group">
                                <span className="flex items-center gap-2 text-[13px]">
                                    {draft.group?.name}
                                    {draft.group?.kind === "new" && <Tag tone="safe">New</Tag>}
                                </span>
                            </ReviewRow>
                            <ReviewRow title="Truck">
                                <span className="text-[13px]">{draft.truckName.trim()}</span>
                            </ReviewRow>
                            <ReviewRow title="Mounted on">
                                <span className="text-[13px]">
                                    {draft.mountedOn ? MountedOnTypeDisplayNames[draft.mountedOn] : "—"}
                                </span>
                            </ReviewRow>
                            <ReviewRow title="Mounting" sub="Height and offset from centre">
                                <span className="text-[13px] tabular-nums">
                                    {feetLabel(draft.heightFeet)} · {feetLabel(draft.offsetFeet)} off centre
                                </span>
                            </ReviewRow>
                            <ReviewRow title="Protected area">
                                <span className="text-[13px] tabular-nums">
                                    {feetLabel(draft.widthFeet)} × {feetLabel(draft.lengthFeet)}
                                </span>
                            </ReviewRow>
                            <ReviewRow title="Impending collision" sub={getCollisionSensitivityDisplayName(draft.sensitivity)}>
                                <span className="text-[13px] tabular-nums">
                                    {draft.approachMph} mph · {draft.reactionSeconds} s
                                </span>
                            </ReviewRow>
                            <ReviewRow title="Speeding">
                                <span className="text-[13px] tabular-nums">
                                    {draft.speedingEnabled ? `On · ${draft.speedingMph} mph` : "Off"}
                                </span>
                            </ReviewRow>
                            <ReviewRow title="Side pass">
                                <span className="text-[13px] tabular-nums">
                                    {draft.leftPassEnabled || draft.rightPassEnabled
                                        ? [
                                              draft.leftPassEnabled ? `left ${draft.leftPassLanes}` : undefined,
                                              draft.rightPassEnabled ? `right ${draft.rightPassLanes}` : undefined,
                                          ]
                                              .filter(Boolean)
                                              .join(" · ") + ` lane(s) · ${draft.sidePassMph} mph`
                                        : "Off"}
                                </span>
                            </ReviewRow>

                            <Note>
                                Pressing Add device creates the location, attaches the unit, draws the protected area and arms
                                these alerts. Everything here stays editable on the truck's settings afterwards.
                            </Note>
                        </>
                    )}

                    {blocker && !error && step !== "key" && (
                        <p className="text-[12px] text-[var(--muted)]">{blocker}</p>
                    )}
                    {error && <Note tone="crit">{error}</Note>}
                </>
            )}
            </>
            )}
        </Modal>
    );
};

/** The equipment types offered, in the order a fleet is most likely to need them. */
const MOUNTED_ON_CHOICES: MountedOnType[] = [
    MountedOnType.TmaTruck,
    MountedOnType.TrailerAttenuator,
    MountedOnType.ShadowVehicle,
    MountedOnType.ArrowBoardTrailer,
    MountedOnType.VmsSignTrailer,
    MountedOnType.Other,
];

const SENSITIVITY_STOPS: { key: string; label: string }[] = [
    { key: String(CollisionSensitivity.Lowest), label: "Lowest" },
    { key: String(CollisionSensitivity.Low), label: "Low" },
    { key: String(CollisionSensitivity.Medium), label: "Medium" },
    { key: String(CollisionSensitivity.High), label: "High" },
    { key: String(CollisionSensitivity.Highest), label: "Highest" },
];

/** Speeds are typed in mph and stored in m/s; the conversion happens once, at commit. */
const MphInput: React.FC<{ value: string; onChange: (v: string) => void; label: string }> = ({
    value,
    onChange,
    label,
}) => (
    <span className="flex items-center gap-2">
        <TextInput
            value={value}
            onChange={(e) => onChange(e.target.value)}
            inputMode="numeric"
            className="w-[80px] text-right"
            aria-label={`${label} in mph`}
        />
        <span className="text-[13px] text-[var(--muted)]">mph</span>
    </span>
);

const LaneStepper: React.FC<{ value: number; onChange: (v: number) => void; side: string }> = ({
    value,
    onChange,
    side,
}) => (
    <span className="flex items-center gap-1">
        <Btn
            tone="quiet"
            onClick={() => onChange(Math.max(MIN_SIDE_PASS_LANES, value - 1))}
            disabled={value <= MIN_SIDE_PASS_LANES}
            aria-label={`Fewer ${side.toLowerCase()} pass lanes`}
        >
            −
        </Btn>
        <span className="w-6 text-center text-[13px] tabular-nums">{value}</span>
        <Btn
            tone="quiet"
            onClick={() => onChange(Math.min(MAX_SIDE_PASS_LANES, value + 1))}
            disabled={value >= MAX_SIDE_PASS_LANES}
            aria-label={`More ${side.toLowerCase()} pass lanes`}
        >
            +
        </Btn>
    </span>
);

/**
 * Where the director is in the sequence.
 *
 * Deliberately dots rather than "Step 3 of 7": the count is the useful part (this is short, and it
 * ends), while numbering invites reading them as a checklist that can be jumped around, which the
 * dependency order does not allow.
 */
const StepDots: React.FC<{ current: number }> = ({ current }) => (
    <div className="flex items-center gap-1.5" aria-hidden>
        {WIZARD_STEPS.map((s, i) => (
            <span
                key={s}
                className={`h-1 flex-1 rounded-full ${
                    i < current ? "bg-[var(--accent)]" : i === current ? "bg-[var(--text)]" : "bg-[var(--line-2)]"
                }`}
            />
        ))}
    </div>
);
